import { useMemo, useState } from 'react'
import { useQuery, useZero } from '@rocicorp/zero/react'
import { dateInputToUtcMs } from '../lib/fine-import/parse.ts'
import { utcMsToDateInput } from '../lib/format.ts'
import { generatePin, isValidPin } from '../lib/pins.ts'
import { mutators } from '../zero/mutators.ts'
import { queries } from '../zero/queries.ts'
import type { PinRow, UserPrivateRow, UserRow } from '../zero/schema.ts'

type MutationOutcome =
  | { readonly type: 'success' }
  | { readonly type: 'error'; readonly error: { readonly message: string } }
interface MutationHandle {
  client: Promise<MutationOutcome>
  server: Promise<MutationOutcome>
}

/** Every field the form manages, as strings so inputs stay controlled. */
interface FormState {
  displayName: string
  fullName: string
  pin: string
  email: string
  dateOfBirth: string
  address: string
  mobileNumber: string
  emergencyContactName: string
  emergencyContactPhone: string
  medicalInfo: string
  shirtSize: string
  shortSize: string
  shoeSize: string
  nameOnBack: string
  numberOnBack: string
  isAdmin: boolean
  isApproved: boolean
}

const EMPTY: FormState = {
  displayName: '',
  fullName: '',
  pin: '',
  email: '',
  dateOfBirth: '',
  address: '',
  mobileNumber: '',
  emergencyContactName: '',
  emergencyContactPhone: '',
  medicalInfo: '',
  shirtSize: '',
  shortSize: '',
  shoeSize: '',
  nameOnBack: '',
  numberOnBack: '',
  isAdmin: false,
  isApproved: false,
}

export function MemberForm({
  member,
  privateRow,
  pin,
  takenPins,
  onClose,
}: {
  /** Absent when creating. */
  member?: UserRow
  privateRow?: UserPrivateRow
  pin?: PinRow
  takenPins: ReadonlySet<string>
  onClose: () => void
}) {
  const z = useZero()
  const editing = member != null

  // The welfare/kit tier syncs only while this form is open — that is why it is
  // a separate table from the roster-wide private one.
  const [profile, profileResult] = useQuery(
    member ? queries.memberProfile({ userId: member.id }) : false,
  )

  const initial = useMemo<FormState>(() => {
    if (!member) return EMPTY
    return {
      displayName: member.displayName,
      fullName: privateRow?.fullName ?? '',
      pin: pin?.pinCode ?? '',
      email: privateRow?.email ?? '',
      dateOfBirth: utcMsToDateInput(privateRow?.dateOfBirth),
      address: profile?.address ?? '',
      mobileNumber: profile?.mobileNumber ?? '',
      emergencyContactName: profile?.emergencyContactName ?? '',
      emergencyContactPhone: profile?.emergencyContactPhone ?? '',
      medicalInfo: profile?.medicalInfo ?? '',
      shirtSize: profile?.shirtSize ?? '',
      shortSize: profile?.shortSize ?? '',
      shoeSize: profile?.shoeSize ?? '',
      nameOnBack: member.nameOnBack ?? '',
      numberOnBack: member.numberOnBack == null ? '' : String(member.numberOnBack),
      isAdmin: member.isAdmin,
      isApproved: member.isApproved,
    }
  }, [member, privateRow, pin, profile])

  const [form, setForm] = useState<FormState | null>(null)
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)

  // Until the admin edits something, mirror the synced values so another
  // admin's change (or the profile query hydrating) is not shown as stale.
  const value = form ?? initial
  const set = <K extends keyof FormState>(key: K, next: FormState[K]) =>
    setForm({ ...value, [key]: next })

  // Submitting a half-loaded profile would blank the member's welfare data.
  const profileReady = !editing || profileResult.type === 'complete'

  async function submit(event: React.FormEvent) {
    event.preventDefault()
    const displayName = value.displayName.trim()
    if (!displayName) {
      setMessage('Enter a display name')
      return
    }
    if (!isValidPin(value.pin)) {
      setMessage('A PIN must contain exactly four digits')
      return
    }
    const dateOfBirth = value.dateOfBirth ? dateInputToUtcMs(value.dateOfBirth) : null
    if (value.dateOfBirth && dateOfBirth == null) {
      setMessage('Enter a valid date of birth')
      return
    }
    const numberOnBack = value.numberOnBack === '' ? null : Number(value.numberOnBack)
    if (numberOnBack != null && (!Number.isInteger(numberOnBack) || numberOnBack < 0 || numberOnBack > 99)) {
      setMessage('A jersey number must be between 0 and 99')
      return
    }

    const payload = {
      displayName,
      pin: value.pin,
      fullName: value.fullName.trim(),
      email: value.email.trim(),
      dateOfBirth,
      address: value.address.trim(),
      mobileNumber: value.mobileNumber.trim(),
      medicalInfo: value.medicalInfo.trim(),
      emergencyContactName: value.emergencyContactName.trim(),
      emergencyContactPhone: value.emergencyContactPhone.trim(),
      shirtSize: value.shirtSize.trim(),
      shortSize: value.shortSize.trim(),
      shoeSize: value.shoeSize.trim(),
      nameOnBack: value.nameOnBack.trim(),
      numberOnBack,
      isAdmin: value.isAdmin,
      isApproved: value.isApproved,
    }

    setBusy(true)
    setMessage('')
    try {
      const mutation: MutationHandle = editing
        ? z.mutate(mutators.members.update({ userId: member.id, ...payload }))
        : z.mutate(mutators.members.create({ userId: crypto.randomUUID(), ...payload }))

      const clientResult = await mutation.client
      if (clientResult.type === 'error') {
        setMessage(clientResult.error.message)
        return
      }
      setMessage(editing ? 'Member saved · syncing…' : 'Member created · syncing…')
      void mutation.server.then((serverResult) => {
        if (serverResult.type === 'success') onClose()
        else setMessage(serverResult.error.message)
      })
    } catch {
      setMessage('Failed to save member')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form className="card form-stack" onSubmit={submit}>
      <h2>{editing ? `Edit ${member.displayName}` : 'Add member'}</h2>

      <fieldset className="field-group">
        <legend>Basic</legend>
        <div className="form-grid">
          <label>
            Display name *
            <input
              maxLength={200}
              value={value.displayName}
              onChange={(event) => set('displayName', event.target.value)}
              required
            />
          </label>
          <label>
            Full name
            <input
              maxLength={200}
              value={value.fullName}
              onChange={(event) => set('fullName', event.target.value)}
            />
          </label>
        </div>
      </fieldset>

      <fieldset className="field-group">
        <legend>PIN</legend>
        <div className="pin-row">
          <label>
            Four-digit PIN *
            <input
              inputMode="numeric"
              pattern="\d{4}"
              maxLength={4}
              value={value.pin}
              onChange={(event) => set('pin', event.target.value.replace(/\D/g, ''))}
              required
            />
          </label>
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              try {
                set('pin', generatePin(takenPins))
                setMessage('')
              } catch (err) {
                setMessage(err instanceof Error ? err.message : 'Could not generate a PIN')
              }
            }}
          >
            Generate
          </button>
        </div>
      </fieldset>

      <fieldset className="field-group">
        <legend>Personal</legend>
        <div className="form-grid">
          <label>
            Email
            <input
              type="email"
              maxLength={320}
              value={value.email}
              onChange={(event) => set('email', event.target.value)}
            />
          </label>
          <label>
            Date of birth
            <input
              type="date"
              value={value.dateOfBirth}
              onChange={(event) => set('dateOfBirth', event.target.value)}
            />
          </label>
        </div>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          PIN recovery matches email plus date of birth. Without both, this member has to
          ask an admin.
        </p>
        <div className="form-grid">
          <label>
            Address
            <input
              maxLength={500}
              value={value.address}
              onChange={(event) => set('address', event.target.value)}
            />
          </label>
          <label>
            Mobile
            <input
              maxLength={50}
              value={value.mobileNumber}
              onChange={(event) => set('mobileNumber', event.target.value)}
            />
          </label>
        </div>
      </fieldset>

      <fieldset className="field-group">
        <legend>Emergency</legend>
        <div className="form-grid">
          <label>
            Contact name
            <input
              maxLength={200}
              value={value.emergencyContactName}
              onChange={(event) => set('emergencyContactName', event.target.value)}
            />
          </label>
          <label>
            Contact phone
            <input
              maxLength={50}
              value={value.emergencyContactPhone}
              onChange={(event) => set('emergencyContactPhone', event.target.value)}
            />
          </label>
        </div>
        <label>
          Medical info
          <textarea
            maxLength={2000}
            rows={2}
            value={value.medicalInfo}
            onChange={(event) => set('medicalInfo', event.target.value)}
          />
        </label>
      </fieldset>

      <fieldset className="field-group">
        <legend>Kit</legend>
        <div className="form-grid">
          <label>
            Shirt
            <input
              maxLength={20}
              value={value.shirtSize}
              onChange={(event) => set('shirtSize', event.target.value)}
            />
          </label>
          <label>
            Shorts
            <input
              maxLength={20}
              value={value.shortSize}
              onChange={(event) => set('shortSize', event.target.value)}
            />
          </label>
          <label>
            Shoes
            <input
              maxLength={20}
              value={value.shoeSize}
              onChange={(event) => set('shoeSize', event.target.value)}
            />
          </label>
        </div>
      </fieldset>

      <fieldset className="field-group">
        <legend>Jersey</legend>
        <div className="form-grid">
          <label>
            Name on back
            <input
              maxLength={200}
              value={value.nameOnBack}
              onChange={(event) => set('nameOnBack', event.target.value)}
            />
          </label>
          <label>
            Number on back (0–99)
            <input
              type="number"
              min="0"
              max="99"
              step="1"
              value={value.numberOnBack}
              onChange={(event) => set('numberOnBack', event.target.value)}
            />
          </label>
        </div>
      </fieldset>

      <fieldset className="field-group">
        <legend>Admin</legend>
        <div className="checkbox-row">
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={value.isAdmin}
              onChange={(event) => set('isAdmin', event.target.checked)}
            />
            Is admin
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={value.isApproved}
              onChange={(event) => set('isApproved', event.target.checked)}
            />
            Is approved
          </label>
        </div>
      </fieldset>

      <div className="form-actions">
        <button className="btn" type="submit" disabled={busy || !profileReady}>
          {busy ? 'Saving…' : editing ? 'Save member' : 'Create member'}
        </button>
        <button className="btn btn-secondary" type="button" onClick={onClose}>
          Cancel
        </button>
      </div>
      {!profileReady ? <p className="form-message">Loading member details…</p> : null}
      {message ? <p className="form-message">{message}</p> : null}
    </form>
  )
}
